// PO10 (docs/build-briefs/PO10-vendor-price-list-import.md; DEC-294; design/
// mockups/vendor-price-list-import.html): the "Update prices from a file" screen
// on the viewed vendor's card in Settings > Purchasing > Vendors. Three steps:
// choose the file, check the prices (a server-computed preview, nothing saved),
// update (one confirm, one apply). This file draws what the server sent and
// sends back only which rows to leave out; every price, match, percent and the
// Check first flag is the server's. The logic that needs no DOM (tabs, filters,
// the Update or Skip state, the words for each refusal) is
// ui/src/vendorPriceListModel.ts and is unit tested on its own.
//
// An admin screen (a keyboard and pointer may be assumed), built touch-first:
// every target is at least 44px, nothing depends on hover, there is NO key
// handler anywhere in this file, and nothing is typed (the file comes from the
// browser's own picker; columns, filters, sort and Update or Skip are taps).
// Text is set with textContent only: item and brand names are user-entered and
// one can be very long, so names wrap and are never cut.
//
// The screen is drawn into #admin-price-list-screen and, while it is open, the
// rest of Settings is hidden by a class on #admin-screen, so it reads as one
// full page with a back button to Vendors. Sign-out calls resetVendorPriceList()
// (admin.ts's invalidate()), which closes it. Unresolved original applies remain
// bound to the actual actor, API, tenant, location and vendor for recovery.

import {
  applyVendorPriceList,
  getVendorLastPriceListImport,
  getVendorPriceListChangesCsv,
  listVendorPriceListIgnored,
  previewVendorPriceList,
  type PriceListApplyResultDto,
  type PriceListIgnoredRowDto,
  type PriceListLastImportDto,
  type PriceListPreviewDto,
  type PriceListRowDto,
  type VendorDto,
} from "./api.ts";
import { formatCents } from "./money.ts";
import { hasPermission, type Session } from "./session.ts";
import {
  PAGE_SIZE,
  TABS,
  applyBody,
  dateTimeText,
  direction,
  errorView,
  fileSizeText,
  filterChanges,
  heldFlagText,
  ignoredLine,
  isOnUpdate,
  percentText,
  plural,
  PRICE_COLUMN_LABEL,
  fillCodeInUseElsewhere,
  initialChoices,
  setFill,
  priceListCrumb,
  priceStatusText,
  priceOrNone,
  problemRowDetail,
  rowsInGroup,
  selectionCounts,
  sortChanges,
  staleLine,
  tabCount,
  toggle,
  type ChangeFilter,
  type ChangeSort,
  type Choices,
  type ErrorView,
  type Tab,
} from "./vendorPriceListModel.ts";

export const PRICE_LIST_PERMISSION = "inv.vendor_price_list.import";

// ---------------------------------------------------------------------
// Wiring supplied by admin.ts
// ---------------------------------------------------------------------

export interface VendorPriceListHooks {
  getSession: () => Session | undefined;
  getPlanningLocationId: () => string | undefined;
  /** Back to where the screen was opened from: the menu's vendor list, or
   * Settings, Vendors. */
  onClose: () => void;
  /** DOCSBUG7: true when the screen was opened from the account menu's Ordering
   * group, false when from a vendor card in Settings, Vendors. */
  openedFromMenu: () => boolean;
  /** The shared confirm sheet. Resolves true on the confirm button. */
  confirm: (options: {
    title: string;
    message: string;
    confirmLabel: string;
    cancelLabel: string;
    tone: "accent";
    extra: HTMLElement;
  }) => Promise<boolean>;
  toast: (message: string, tone: "danger" | "success" | "neutral") => void;
}

let hooks: VendorPriceListHooks | undefined;

export function configureVendorPriceList(next: VendorPriceListHooks): void {
  hooks = next;
}

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) {
    throw new Error(`expected element for selector "${selector}"`);
  }
  return element;
}

const adminScreenEl = mustFind<HTMLElement>("#admin-screen");
const rootEl = mustFind<HTMLElement>("#admin-price-list-screen");
const OPEN_CLASS = "admin-price-list-open";

// ---------------------------------------------------------------------
// State
// ---------------------------------------------------------------------

type Phase = "choose" | "reading" | "preview" | "applying" | "done" | "error";

interface ErrorState extends ErrorView {
  /** Where the refusal came from, so the buttons offer the right next tap. */
  step: "choose" | "update";
}

interface State {
  session: Session;
  locationId: string | undefined;
  originalBody: (ReturnType<typeof applyBody> & { locationId?: string }) | undefined;
  vendor: { id: string; name: string };
  phase: Phase;
  file: File | undefined;
  /** A column the user tapped after a "no column" refusal. */
  codeColumn: string | undefined;
  priceColumn: string | undefined;
  preview: PriceListPreviewDto | undefined;
  tab: Tab;
  filter: ChangeFilter;
  sort: ChangeSort;
  /** How many rows each long list shows, in pages of 25. */
  shown: Record<string, number>;
  choices: Choices;
  /** One key per Review tap, reused by Try again so a retry can never double apply. */
  applyKey: string | undefined;
  /** The gate said this is not the right file; "Show what matched" lifts the card. */
  gateShown: boolean;
  ignored: { rows: PriceListIgnoredRowDto[]; total: number; next: number | null; loading: boolean };
  result: PriceListApplyResultDto | undefined;
  error: ErrorState | undefined;
}

let state: State | undefined;
// Unresolved original bodies survive sheet teardown and same-actor renewed sign-in.
const originalApplies = new Map<string, State>();
function scopeKey(active: Session, locationId: string | undefined, vendorId: string): string {
  return JSON.stringify([
    active.apiBaseUrl,
    active.tenantId,
    active.userId,
    locationId ?? null,
    vendorId,
  ]);
}
function currentActor(s: State): boolean {
  const active = hooks?.getSession();
  return (
    active !== undefined &&
    active.apiBaseUrl === s.session.apiBaseUrl &&
    active.tenantId === s.session.tenantId &&
    active.userId === s.session.userId &&
    hooks?.getPlanningLocationId() === s.locationId
  );
}

/** Bumped by reset and by every open, so an answer that lands after either is dropped. */
let generation = 0;

const lastImports = new Map<string, PriceListLastImportDto | null>();
const lastImportLoading = new Set<string>();

function isCurrent(gen: number): boolean {
  return (
    gen === generation &&
    state !== undefined &&
    currentActor(state) &&
    hooks?.getSession()?.token === state.session.token
  );
}

// ---------------------------------------------------------------------
// Open, close, reset
// ---------------------------------------------------------------------

function closeScreenDom(): void {
  adminScreenEl.classList.remove(OPEN_CLASS);
  rootEl.hidden = true;
  rootEl.replaceChildren();
}

/** Sign-out clears the visible sheet; actor-bound original applies remain recoverable. */
export function resetVendorPriceList(): void {
  generation += 1;
  state = undefined;
  lastImports.clear();
  lastImportLoading.clear();
  closeScreenDom();
}

function closeScreen(): void {
  generation += 1;
  const vendorId = state?.vendor.id;
  state = undefined;
  if (vendorId !== undefined) {
    lastImports.delete(vendorId);
  }
  closeScreenDom();
  hooks?.onClose();
}

function scrollToTop(): void {
  document.querySelector<HTMLElement>("#app-shell")?.scrollTo({ top: 0, behavior: "instant" });
  window.scrollTo(0, 0);
}

export function openVendorPriceListScreen(session: Session, vendor: VendorDto): void {
  generation += 1;
  const locationId = hooks?.getPlanningLocationId();
  state =
    originalApplies.get(scopeKey(session, locationId, vendor.id)) ??
    freshState(session, { id: vendor.id, name: vendor.name });
  state.session = session;
  adminScreenEl.classList.add(OPEN_CLASS);
  rootEl.hidden = false;
  render();
  scrollToTop();
}

function freshState(session: Session, vendor: { id: string; name: string }): State {
  return {
    session,
    locationId: hooks?.getPlanningLocationId(),
    originalBody: undefined,
    vendor,
    phase: "choose",
    file: undefined,
    codeColumn: undefined,
    priceColumn: undefined,
    preview: undefined,
    tab: "changes",
    filter: "all",
    sort: "check",
    shown: {},
    choices: { skipped: new Set(), optedIn: new Set(), fillCodes: new Map() },
    applyKey: undefined,
    gateShown: false,
    ignored: { rows: [], total: 0, next: null, loading: false },
    result: undefined,
    error: undefined,
  };
}

// ---------------------------------------------------------------------
// The vendor card entry: the "last updated" line and the button
// ---------------------------------------------------------------------

/** Price import for this vendor's configured format and the current import permission. */
export function buildPriceListCardEntry(session: Session, vendor: VendorDto): HTMLElement | null {
  if (vendor.price_file_layout !== "nc_abc" || !hasPermission(session, PRICE_LIST_PERMISSION)) {
    return null;
  }
  const wrap = h("div", { cls: "pl-card-entry", testid: "admin-vendor-price-list-entry" });
  const line = h("p", {
    cls: "admin-field-help pl-card-last",
    testid: "admin-vendor-price-list-last",
    attrs: { "data-vendor-id": vendor.id },
  });
  const button = h("button", {
    cls: "btn-primary pl-card-open",
    text: "Update prices from a file",
    testid: "admin-vendor-price-list-open",
    attrs: { type: "button" },
  });
  button.addEventListener("click", () => openVendorPriceListScreen(session, vendor));
  wrap.append(line, button);
  paintLastImport(line, vendor.id);
  if (!lastImports.has(vendor.id) && !lastImportLoading.has(vendor.id)) {
    lastImportLoading.add(vendor.id);
    void loadLastImport(session, vendor.id);
  }
  return wrap;
}

/** UX120: the same short value on the collapsed vendor row. Null where the card entry is not drawn. */
export function buildPriceListSummary(session: Session, vendor: VendorDto): HTMLElement | null {
  if (vendor.price_file_layout !== "nc_abc" || !hasPermission(session, PRICE_LIST_PERMISSION)) {
    return null;
  }
  const value = h("span", {
    cls: "admin-vendor-summary-prices",
    testid: "admin-vendor-price-summary",
    attrs: { "data-vendor-id": vendor.id },
  });
  paintLastImport(value, vendor.id);
  return value;
}

function paintLastImport(line: HTMLElement, vendorId: string): void {
  const record = lastImports.get(vendorId);
  line.textContent = record === undefined ? "" : priceStatusText(record);
}

async function loadLastImport(session: Session, vendorId: string): Promise<void> {
  const gen = generation;
  const result = await getVendorLastPriceListImport(session.apiBaseUrl, session.tenantId, vendorId);
  lastImportLoading.delete(vendorId);
  if (gen !== generation || result.status !== 200) {
    return;
  }
  lastImports.set(vendorId, result.body.import);
  for (const line of document.querySelectorAll<HTMLElement>(
    `[data-testid="admin-vendor-price-list-last"][data-vendor-id="${vendorId}"],` +
      `[data-testid="admin-vendor-price-summary"][data-vendor-id="${vendorId}"]`,
  )) {
    paintLastImport(line, vendorId);
  }
}

// ---------------------------------------------------------------------
// Small DOM helpers
// ---------------------------------------------------------------------

interface Opts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function h(tag: string, opts: Opts = {}, children: Array<Node | string> = []): HTMLElement {
  const element = document.createElement(tag);
  if (opts.cls !== undefined) element.className = opts.cls;
  if (opts.text !== undefined) element.textContent = opts.text;
  if (opts.testid !== undefined) element.dataset["testid"] = opts.testid;
  for (const [key, value] of Object.entries(opts.attrs ?? {})) {
    element.setAttribute(key, value);
  }
  for (const child of children) {
    element.append(child);
  }
  return element;
}

const SVG_NS = "http://www.w3.org/2000/svg";

type IconName = "arrow-left" | "info" | "check" | "up" | "down" | "alert" | "file" | "check-circle";

const ICON_PATHS: Record<IconName, string[]> = {
  "arrow-left": ["M19 12H5", "M12 19l-7-7 7-7"],
  info: ["M12 8h.01", "M11 12h1v5h1"],
  check: ["M5 12.5l4.5 4.5L19 7"],
  up: ["M12 19V5", "M6 11l6-6 6 6"],
  down: ["M12 5v14", "M6 13l6 6 6-6"],
  alert: ["M12 8v5", "M12 16.5v.01"],
  file: ["M6 3h8l4 4v14H6z", "M14 3v4h4", "M9 13h6", "M9 17h6"],
  "check-circle": ["M8.5 12.5l2.5 2.5 4.5-5"],
};

function icon(name: IconName, large = false): SVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("class", large ? "pl-ico pl-ico-lg" : "pl-ico");
  if (name === "info" || name === "alert" || name === "check-circle") {
    const circle = document.createElementNS(SVG_NS, "circle");
    circle.setAttribute("cx", "12");
    circle.setAttribute("cy", "12");
    circle.setAttribute("r", "9");
    circle.setAttribute("fill", "none");
    circle.setAttribute("stroke", "currentColor");
    circle.setAttribute("stroke-width", "1.8");
    svg.append(circle);
  }
  for (const d of ICON_PATHS[name]) {
    const path = document.createElementNS(SVG_NS, "path");
    path.setAttribute("d", d);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1.8");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    svg.append(path);
  }
  return svg;
}

function button(
  label: string,
  testid: string,
  onTap: () => void,
  cls = "pl-btn",
  disabled = false,
): HTMLButtonElement {
  const element = h("button", { cls, text: label, testid, attrs: { type: "button" } });
  const el = element as HTMLButtonElement;
  el.disabled = disabled;
  el.addEventListener("click", onTap);
  return el;
}

// ---------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------

function render(): void {
  const s = state;
  if (s === undefined) {
    return;
  }
  const shell = document.querySelector<HTMLElement>("#app-shell");
  const scrollTop = shell?.scrollTop ?? 0;
  const focused = document.activeElement as HTMLElement | null;
  const focusKey =
    focused !== null && rootEl.contains(focused)
      ? { testid: focused.dataset["testid"], offer: focused.dataset["offerId"] }
      : undefined;

  rootEl.replaceChildren(...screenNodes(s));

  shell?.scrollTo({ top: scrollTop, behavior: "instant" });
  if (focusKey?.testid !== undefined) {
    const selector =
      focusKey.offer === undefined
        ? `[data-testid="${focusKey.testid}"]`
        : `[data-testid="${focusKey.testid}"][data-offer-id="${focusKey.offer}"]`;
    rootEl.querySelector<HTMLElement>(selector)?.focus({ preventScroll: true });
  }
}

function screenNodes(s: State): Node[] {
  const nodes: Node[] = [];
  const back = button("Back to vendors", "price-list-back", closeScreen, "pl-btn pl-back");
  back.prepend(icon("arrow-left"));
  nodes.push(
    h("div", { cls: "pl-crumb" }, [
      back,
      h("span", {
        text: priceListCrumb(hooks?.openedFromMenu() === true ? "menu" : "settings", s.vendor.name),
        testid: "price-list-crumb",
      }),
    ]),
  );
  const title =
    s.phase === "preview" || s.phase === "applying"
      ? "Check the new prices"
      : `Update ${s.vendor.name} prices`;
  nodes.push(h("h1", { cls: "pl-page-title", text: title, testid: "price-list-title" }));
  if (
    s.phase === "choose" ||
    s.phase === "reading" ||
    (s.phase === "error" && s.error?.step === "choose")
  ) {
    nodes.push(
      h("p", {
        cls: "pl-page-sub",
        text: "Pick the NC ABC price list. You will see every change before anything is saved.",
      }),
    );
  }
  nodes.push(stepper(s));

  switch (s.phase) {
    case "choose":
    case "reading":
      nodes.push(...chooseNodes(s));
      break;
    case "error":
      nodes.push(...errorNodes(s));
      break;
    case "preview":
    case "applying":
      nodes.push(...previewNodes(s));
      break;
    case "done":
      nodes.push(...doneNodes(s));
      break;
  }
  return nodes;
}

function stepper(s: State): HTMLElement {
  const stage =
    s.phase === "preview" || s.phase === "applying"
      ? 2
      : s.phase === "done"
        ? 4
        : s.phase === "error" && s.error?.step === "update"
          ? 3
          : 1;
  const labels = ["Choose the file", "Check the prices", "Update"];
  const list = h("ol", { cls: "pl-steps", testid: "price-list-steps" });
  labels.forEach((label, index) => {
    const n = index + 1;
    const done = stage > n;
    const on = stage === n;
    const mark = h("span", { cls: "pl-step-n" });
    if (done) {
      mark.append(icon("check"));
    } else {
      mark.textContent = String(n);
    }
    list.append(
      h("li", { cls: `pl-step${on ? " pl-step--on" : ""}${done ? " pl-step--done" : ""}` }, [
        mark,
        h("span", { text: label }),
      ]),
    );
  });
  return list;
}

// ---------------------------------------------------------------------
// Step 1: choose the file
// ---------------------------------------------------------------------

function chooseNodes(s: State): Node[] {
  const reading = s.phase === "reading";
  const input = h("input", {
    cls: "pl-sr",
    testid: "price-list-file-input",
    attrs: { type: "file", accept: ".xlsx,.csv", id: "price-list-file-input" },
  }) as HTMLInputElement;
  input.addEventListener("change", () => {
    const picked = input.files?.[0];
    if (picked === undefined || state === undefined) return;
    state.file = picked;
    state.codeColumn = undefined;
    state.priceColumn = undefined;
    state.error = undefined;
    state.phase = "choose";
    render();
  });

  const picker = h("div", {
    cls: `pl-picker${s.file === undefined ? "" : " pl-picker--chosen"}`,
    testid: "price-list-picker",
  });
  picker.append(icon("file", true));
  if (s.file === undefined) {
    picker.append(
      h("div", { cls: "pl-picker-text" }, [
        h("div", { cls: "pl-picker-name", text: "No file chosen yet" }),
        h("div", { cls: "pl-picker-sub", text: "An Excel (.xlsx) or CSV file up to 5 MB." }),
      ]),
    );
  } else {
    picker.append(
      h("div", { cls: "pl-picker-text" }, [
        h("div", { cls: "pl-picker-name", text: s.file.name, testid: "price-list-file-name" }),
        h("div", { cls: "pl-picker-sub", text: fileSizeText(s.file.size) }),
      ]),
    );
  }
  const label = h("label", {
    cls: "pl-btn",
    text: s.file === undefined ? "Choose a file" : "Choose a different file",
    testid: "price-list-choose-label",
    attrs: { for: "price-list-file-input" },
  });
  picker.append(label, input);

  const check = button(
    reading ? "Checking the file" : "Check the prices",
    "price-list-check",
    () => void runPreview(),
    "pl-btn-primary",
    s.file === undefined || reading,
  );
  const left = h("section", { cls: "pl-card" }, [
    h("h2", { text: "The price list file" }),
    h("p", {
      text: "Use the same spreadsheet you already get from NC ABC. We match its columns by their names; if one looks wrong you can tap another after the check.",
    }),
    picker,
    h("div", { cls: "pl-cta-row" }, [
      check,
      h("span", { cls: "pl-fine", text: "Nothing is saved yet." }),
    ]),
  ]);

  const does = h("section", { cls: "pl-card" }, [
    h("h2", { text: "What this does" }),
    h("ul", { cls: "pl-plain-list" }, [
      h("li", {}, [
        "Finds each item on ",
        h("strong", { text: s.vendor.name }),
        " by its ",
        h("strong", { text: "NC code" }),
        ".",
      ]),
      h("li", { text: "Adds the NC code to an item that has none, when the name and size match." }),
      h("li", { text: "Shows the price now and the new price, with the % change." }),
      h("li", { text: "Saves only the prices you leave switched on." }),
    ]),
    h("h2", { cls: "pl-never-title", text: "What it never changes" }),
    neverList(s.vendor.name),
  ]);
  return [h("div", { cls: "pl-two" }, [left, does])];
}

function neverList(vendorName: string): HTMLElement {
  const items: Array<[string, string]> = [
    ["On-hand stock.", "No counts or bottles change."],
    ["Average cost.", "What your bottles cost you in reports stays as it is."],
    ["Orders already placed.", "Their totals stay as they were."],
    [
      "Items, pars, drink prices and the preferred vendor.",
      `Only the listed price, and an NC code an item does not have yet, on ${vendorName}. An NC code already there stays.`,
    ],
  ];
  const list = h("ul", { cls: "pl-never" });
  for (const [bold, rest] of items) {
    const row = h("li", {}, [
      icon("check-circle"),
      h("span", {}, [h("strong", { text: bold }), ` ${rest}`]),
    ]);
    list.append(row);
  }
  return list;
}

async function runPreview(): Promise<void> {
  const s = state;
  if (s === undefined || s.file === undefined || s.originalBody !== undefined || !currentActor(s))
    return;
  const gen = generation;
  s.phase = "reading";
  s.error = undefined;
  render();
  const result = await previewVendorPriceList(
    s.session.apiBaseUrl,
    s.session.tenantId,
    s.vendor.id,
    s.file,
    {
      ...(s.locationId === undefined ? {} : { locationId: s.locationId }),
      ...(s.codeColumn === undefined ? {} : { codeColumn: s.codeColumn }),
      ...(s.priceColumn === undefined ? {} : { priceColumn: s.priceColumn }),
    },
  );
  if (!isCurrent(gen) || state === undefined) return;
  if (result.status !== 201) {
    state.phase = "error";
    state.error = {
      ...errorView(result.status, result.networkError, result.body, "choose"),
      step: "choose",
    };
    render();
    return;
  }
  state.preview = result.body;
  state.phase = "preview";
  state.tab = result.body.gate.bootstrap === true ? "skufill" : "changes";
  state.filter = "all";
  state.sort = "check";
  state.shown = {};
  state.choices = initialChoices(result.body);
  state.applyKey = undefined;
  state.gateShown = false;
  state.ignored = { rows: [], total: result.body.summary.ignored, next: 0, loading: false };
  render();
  scrollToTop();
}

// ---------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------

function errorNodes(s: State): Node[] {
  const error = s.error;
  if (error === undefined) return [];
  return [errorCard(s, error)];
}

function errorCard(s: State, error: ErrorState): HTMLElement {
  if (s.originalBody !== undefined) {
    return h("section", { cls: "pl-err", attrs: { role: "alert" } }, [
      h("h2", { text: "Original update is unconfirmed" }),
      h("p", {
        text: "The original file, location and choices are retained. Retry the original update before starting another file. Sign in as the same person at the original location if access changed.",
      }),
      h("p", { text: error.message }),
      button(
        "Retry original update",
        "price-list-retry-original",
        () => void applyNow(),
        "pl-btn-primary",
        !currentActor(s),
      ),
    ]);
  }
  const where = error.step === "choose" ? "Step 1, choosing the file" : "Step 3, updating";
  const card = h("section", {
    cls: `pl-err ${error.kind === "denied" || error.kind === "network" ? "pl-err--danger" : "pl-err--warn"}`,
    testid: "price-list-error",
    attrs: { role: "alert" },
  });
  card.append(
    h("div", { cls: "pl-err-where", text: where }),
    h("h2", { text: error.title, testid: "price-list-error-title" }),
    h("p", { text: error.message, testid: "price-list-error-message" }),
  );
  if ((error.kind === "nocode" || error.kind === "noprice") && error.columns.length > 0) {
    const row = h("div", { cls: "pl-opt-row", testid: "price-list-column-choices" });
    for (const heading of error.columns) {
      row.append(
        button(
          heading,
          "price-list-column-chip",
          () => {
            if (state === undefined) return;
            if (error.kind === "nocode") state.codeColumn = heading;
            else state.priceColumn = heading;
            void runPreview();
          },
          "pl-opt",
        ),
      );
    }
    card.append(row);
  }
  const actions = h("div", { cls: "pl-err-actions" });
  const chooseAgain = (): void => {
    if (state === undefined) return;
    state.preview = undefined;
    state.file = undefined;
    state.error = undefined;
    state.phase = "choose";
    render();
  };
  switch (error.kind) {
    case "network":
      if (error.step === "update") {
        actions.append(
          button("Try again", "price-list-error-primary", () => void applyNow(), "pl-btn-primary"),
          button("Go back", "price-list-error-secondary", () => backToPreview(), "pl-btn"),
        );
      } else {
        actions.append(
          button(
            "Try again",
            "price-list-error-primary",
            () => void runPreview(),
            "pl-btn-primary",
            s.file === undefined,
          ),
          button("Choose a different file", "price-list-error-secondary", chooseAgain, "pl-btn"),
        );
      }
      break;
    case "denied":
    case "novendor":
      actions.append(
        button("Back to vendors", "price-list-error-primary", closeScreen, "pl-btn-primary"),
      );
      break;
    case "expired":
    case "used":
      actions.append(
        button(
          "Check the file again",
          "price-list-error-primary",
          () => void runPreview(),
          "pl-btn-primary",
          s.file === undefined,
        ),
        button("Choose a different file", "price-list-error-secondary", chooseAgain, "pl-btn"),
      );
      break;
    default:
      actions.append(
        button(
          "Choose a different file",
          "price-list-error-primary",
          chooseAgain,
          "pl-btn-primary",
        ),
      );
  }
  card.append(actions);
  return card;
}

function backToPreview(): void {
  if (state === undefined || state.preview === undefined) return;
  state.phase = "preview";
  state.error = undefined;
  render();
}

// ---------------------------------------------------------------------
// Step 2: check the prices
// ---------------------------------------------------------------------

function previewNodes(s: State): Node[] {
  const preview = s.preview;
  if (preview === undefined) return [];
  const nodes: Node[] = [];
  const busy = s.phase === "applying";

  const bannerText = h("p", {}, [
    h("strong", { text: "Preview only. Nothing has been saved. " }),
    `Read ${preview.file.name}: ${plural(preview.file.rowCount, "row", "rows")}, using ${preview.columns.priceColumn}. The new price replaces the price now on each ${s.vendor.name} item. This check is good for 30 minutes.`,
  ]);
  nodes.push(
    h("div", { cls: "pl-note pl-note--info", testid: "price-list-banner" }, [
      icon("info"),
      bannerText,
    ]),
  );

  if (!preview.gate.ok) {
    nodes.push(gateCard(s, preview));
    if (!s.gateShown) {
      return nodes;
    }
  }

  const counts = selectionCounts(preview, s.choices);
  const sum = preview.summary;
  const tiles = h("div", { cls: "pl-tiles", testid: "price-list-tiles" }, [
    tile(
      "ready",
      String(counts.updating),
      "Ready to update",
      `${counts.up} ${counts.up === 1 ? "goes" : "go"} up, ${counts.down} ${counts.down === 1 ? "goes" : "go"} down${counts.firstPrice > 0 ? `, ${counts.firstPrice} first ${counts.firstPrice === 1 ? "price" : "prices"}` : ""}`,
      false,
    ),
    tile("held", String(sum.held), "Check first", "Over 25% change, held back", true),
    tile("nochange", String(sum.noChange), "No change", "Same price as now", false),
    tile("problems", String(sum.problems), "Cannot use", "A problem with the row", true),
  ]);
  nodes.push(tiles);

  const tabs = h("div", { cls: "pl-tabs", testid: "price-list-tabs", attrs: { role: "tablist" } });
  for (const tab of TABS) {
    const count = tabCount(preview, tab.id);
    const on = s.tab === tab.id;
    const flag =
      (tab.id === "problems" || tab.id === "notinfile" || tab.id === "nocode") && count > 0;
    const element = button(
      tab.label,
      `price-list-tab-${tab.id}`,
      () => void chooseTab(tab.id),
      `pl-tab${on ? " pl-tab--on" : ""}${flag ? " pl-tab--flag" : ""}`,
    );
    element.setAttribute("role", "tab");
    element.setAttribute("aria-selected", on ? "true" : "false");
    element.append(h("span", { cls: "pl-tab-n", text: count.toLocaleString("en-US") }));
    tabs.append(element);
  }
  nodes.push(tabs);

  nodes.push(...tabBody(s, preview, busy));
  nodes.push(applyLocation(preview, counts, busy));
  return nodes;
}

function tile(key: string, big: string, label: string, sub: string, warn: boolean): HTMLElement {
  return h(
    "div",
    { cls: `pl-tile${warn ? " pl-tile--warn" : ""}`, testid: `price-list-tile-${key}` },
    [
      h("div", { cls: "pl-tile-n", text: big }),
      h("div", { cls: "pl-tile-l", text: label }),
      h("div", { cls: "pl-tile-s", text: sub }),
    ],
  );
}

function gateCard(s: State, preview: PriceListPreviewDto): HTMLElement {
  const card = h("section", {
    cls: "pl-err pl-err--warn",
    testid: "price-list-gate",
    attrs: { role: "alert" },
  });
  card.append(
    h("div", { cls: "pl-err-where", text: "Step 2, checking the prices" }),
    h("h2", { text: "This does not look like the right file" }),
    h("p", {
      text: `Only ${preview.gate.foundOffers} of the ${preview.gate.codedOffers} ${s.vendor.name} items were found in this file. Updating from it would leave almost everything alone, so Update is switched off.`,
    }),
  );
  const actions = h("div", { cls: "pl-err-actions" }, [
    button(
      "Choose a different file",
      "price-list-gate-choose",
      () => {
        if (state === undefined) return;
        state.preview = undefined;
        state.file = undefined;
        state.phase = "choose";
        render();
      },
      "pl-btn-primary",
    ),
    button(
      s.gateShown ? "Hide what matched" : "Show what matched",
      "price-list-gate-show",
      () => {
        if (state === undefined) return;
        state.gateShown = !state.gateShown;
        render();
      },
      "pl-btn",
    ),
  ]);
  card.append(actions);
  return card;
}

async function chooseTab(tab: Tab): Promise<void> {
  const s = state;
  if (s === undefined) return;
  s.tab = tab;
  render();
  if (tab === "ignored" && s.ignored.rows.length === 0 && s.ignored.total > 0) {
    await loadMoreIgnored();
  }
}

async function loadMoreIgnored(): Promise<void> {
  const s = state;
  if (s === undefined || s.preview === undefined || s.ignored.loading || s.ignored.next === null)
    return;
  const gen = generation;
  s.ignored.loading = true;
  render();
  const result = await listVendorPriceListIgnored(
    s.session.apiBaseUrl,
    s.session.tenantId,
    s.vendor.id,
    s.preview.previewToken,
    s.ignored.next,
    PAGE_SIZE,
  );
  if (!isCurrent(gen) || state === undefined) return;
  state.ignored.loading = false;
  if (result.status === 200) {
    state.ignored.rows = [...state.ignored.rows, ...result.body.rows];
    state.ignored.total = result.body.total;
    state.ignored.next = result.body.nextCursor;
  } else {
    hooks?.toast("Could not load more rows. Check your connection and try again.", "danger");
  }
  render();
}

function shownFor(s: State, key: string): number {
  return s.shown[key] ?? PAGE_SIZE;
}

function showMoreRow(s: State, key: string, total: number, noun: string): HTMLElement {
  const shown = Math.min(shownFor(s, key), total);
  const row = h("div", { cls: "pl-more-row" }, [
    h("span", {
      text: `Showing ${shown.toLocaleString("en-US")} of ${plural(total, noun, `${noun}s`)}.`,
      testid: "price-list-showing",
    }),
  ]);
  if (shown < total) {
    row.append(
      button(
        "Show 25 more",
        "price-list-show-more",
        () => {
          if (state === undefined) return;
          state.shown[key] = shownFor(state, key) + PAGE_SIZE;
          render();
        },
        "pl-btn",
      ),
    );
  }
  return row;
}

function tabBody(s: State, preview: PriceListPreviewDto, busy: boolean): Node[] {
  switch (s.tab) {
    case "changes":
      return changesBody(s, preview, busy);
    case "skufill":
      return skuFillBody(s, preview, busy);
    case "nochange":
      return simpleRowsBody(
        s,
        "nochange",
        "Same price as now. Nothing to do.",
        rowsInGroup(preview, "no_change"),
        "No change",
        (row) => `NC ${row.ncCode}. Price now ${priceOrNone(row.currentCostCents)}.`,
      );
    case "problems":
      return problemsBody(s, preview);
    case "notinfile":
      return simpleRowsBody(
        s,
        "notinfile",
        `You buy these from ${s.vendor.name}, but their NC code is not in this price list. They keep the price they have now. They may be dropped or on allocation.`,
        rowsInGroup(preview, "not_in_file"),
        "Keeps price",
        (row) =>
          `NC ${row.ncCode}. Price now ${priceOrNone(row.currentCostCents)}. Last updated ${new Date(row.lastUpdatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}.`,
      );
    case "nocode":
      return noCodeBody(s, preview);
    case "ignored":
      return ignoredBody(s);
  }
}

function changesBody(s: State, preview: PriceListPreviewDto, busy: boolean): Node[] {
  const all = rowsInGroup(preview, "change");
  const nodes: Node[] = [];
  const filters = h("div", { cls: "pl-filters" });
  const showGroup = h("div", { cls: "pl-filter-group" }, [
    h("span", { cls: "pl-filter-label", text: "Show" }),
  ]);
  const counts: Record<ChangeFilter, number> = {
    all: all.length,
    up: filterChanges(all, "up").length,
    down: filterChanges(all, "down").length,
    check: preview.summary.held,
  };
  const filterLabels: Array<[ChangeFilter, string]> = [
    ["all", "All"],
    ["up", "Went up"],
    ["down", "Went down"],
    ["check", "Check first"],
  ];
  for (const [id, label] of filterLabels) {
    const on = s.filter === id;
    const chip = button(
      `${label} ${counts[id]}`,
      `price-list-filter-${id}`,
      () => {
        if (state === undefined) return;
        state.filter = id;
        state.shown["changes"] = PAGE_SIZE;
        render();
      },
      `pl-opt${on ? " pl-opt--on" : ""}`,
    );
    chip.setAttribute("aria-pressed", on ? "true" : "false");
    showGroup.append(chip);
  }
  const sortGroup = h("div", { cls: "pl-filter-group" }, [
    h("span", { cls: "pl-filter-label", text: "Sort" }),
  ]);
  const sortLabels: Array<[ChangeSort, string]> = [
    ["check", "Check first, then biggest"],
    ["name", "Name"],
  ];
  for (const [id, label] of sortLabels) {
    const on = s.sort === id;
    const chip = button(
      label,
      `price-list-sort-${id}`,
      () => {
        if (state === undefined) return;
        state.sort = id;
        render();
      },
      `pl-opt${on ? " pl-opt--on" : ""}`,
    );
    chip.setAttribute("aria-pressed", on ? "true" : "false");
    sortGroup.append(chip);
  }
  filters.append(showGroup, sortGroup);
  nodes.push(filters);

  const rows = sortChanges(filterChanges(all, s.filter), s.sort);
  if (rows.length === 0) {
    nodes.push(
      h("p", {
        cls: "pl-section-p",
        text:
          all.length === 0
            ? "No prices in this file differ from the prices now."
            : "No rows match this filter.",
        testid: "price-list-empty",
      }),
    );
    return nodes;
  }
  const list = h("ul", { cls: "pl-plist", testid: "price-list-rows" });
  list.append(
    h("li", { cls: "pl-phead", attrs: { "aria-hidden": "true" } }, [
      h("span", { text: "Item" }),
      h("span", { text: "Now" }),
      h("span"),
      h("span", { text: "New" }),
      h("span", { text: "Change" }),
      h("span", { text: "Apply" }),
    ]),
  );
  for (const row of rows.slice(0, shownFor(s, "changes"))) {
    list.append(changeRow(s, row, busy));
  }
  nodes.push(list, showMoreRow(s, "changes", rows.length, "change"));
  return nodes;
}

function changeRow(s: State, row: PriceListRowDto, busy: boolean): HTMLElement {
  const on = isOnUpdate(row, s.choices);
  const item = h("div", { cls: "pl-p-item" }, [
    h("div", { cls: "pl-p-name", text: row.productName }),
    h("div", {
      cls: "pl-p-sub",
      text: `NC ${row.ncCode}.${row.fileBrandName === null ? "" : ` In the file as ${row.fileBrandName}.`}`,
    }),
  ]);
  if (row.held) {
    item.append(
      h("div", { cls: "pl-held-flag", testid: "price-list-row-flag" }, [
        icon("alert"),
        h("span", { text: heldFlagText(row) }),
      ]),
    );
  }
  const prices = h("div", { cls: "pl-p-prices" }, [
    h("span", { cls: "pl-p-old" }, [
      h("span", { cls: "pl-lbl", text: "Now" }),
      row.currentCostCents === null
        ? h("span", { cls: "pl-none", text: "No price" })
        : formatCents(row.currentCostCents),
    ]),
    h("span", { cls: "pl-p-arrow", text: "→", attrs: { "aria-hidden": "true" } }),
    h("span", { cls: "pl-p-new" }, [
      h("span", { cls: "pl-lbl", text: "New" }),
      row.newCostCents === null ? "" : formatCents(row.newCostCents),
    ]),
  ]);
  const d = direction(row);
  const delta = h("span", { cls: "pl-p-delta" });
  if (d === "first") {
    delta.append(h("span", { cls: "pl-delta pl-delta--new", text: "First price" }));
  } else {
    const chip = h("span", { cls: `pl-delta pl-delta--${d === "down" ? "down" : "up"}` });
    if (d === "up") chip.append(icon("up"));
    if (d === "down") chip.append(icon("down"));
    chip.append(percentText(row.changePercentTenths ?? 0));
    delta.append(chip);
  }
  const sw = h("button", {
    cls: "pl-switch pl-p-sw",
    testid: "price-list-row-switch",
    attrs: {
      type: "button",
      role: "switch",
      "aria-checked": on ? "true" : "false",
      "aria-label": `${on ? "Update" : "Skip"} ${row.productName}`,
      "data-offer-id": row.offerId,
    },
  }) as HTMLButtonElement;
  sw.dataset["offerId"] = row.offerId;
  sw.disabled = busy;
  sw.append(
    h("span", { cls: "pl-switch-track" }, [h("span", { cls: "pl-switch-knob" })]),
    h("span", { cls: "pl-switch-text", text: on ? "Update" : "Skip" }),
  );
  sw.addEventListener("click", () => {
    if (state === undefined) return;
    state.choices = toggle(row, state.choices);
    render();
  });
  return h(
    "li",
    {
      cls: `pl-prow${row.held ? " pl-prow--held" : ""}`,
      testid: "price-list-row",
      attrs: { "data-offer-id": row.offerId },
    },
    [item, prices, delta, sw],
  );
}

function skuFillBody(s: State, preview: PriceListPreviewDto, busy: boolean): Node[] {
  const rows = rowsInGroup(preview, "sku_fill");
  const nodes: Node[] = [
    h("p", {
      cls: "pl-section-p",
      text: `These items have no NC code on ${s.vendor.name}. Switch one on to add the code from the file.`,
    }),
  ];
  if (rows.length === 0) {
    nodes.push(h("p", { cls: "pl-section-p", text: "Nothing here.", testid: "price-list-empty" }));
    return nodes;
  }
  const list = h("ul", { cls: "pl-issue-list", testid: "price-list-list-skufill" });
  for (const row of rows.slice(0, shownFor(s, "skufill"))) {
    list.append(skuFillRow(s, row, busy));
  }
  nodes.push(list, showMoreRow(s, "skufill", rows.length, "item"));
  return nodes;
}

function skuFillRow(s: State, row: PriceListRowDto, busy: boolean): HTMLElement {
  const candidates = row.skuFill?.candidates ?? [];
  const chosen = s.choices.fillCodes.get(row.productId);
  const on = chosen !== undefined;
  const picks = h("div", { cls: "pl-opt-row", attrs: { role: "radiogroup" } });
  for (const candidate of candidates) {
    const isChosen = chosen === candidate.ncCode;
    const taken = !isChosen && fillCodeInUseElsewhere(s.choices, row.productId, candidate.ncCode);
    const label = [
      `NC ${candidate.ncCode}`,
      candidate.brandName ?? "",
      candidate.sizeLabel ?? "",
      candidate.priceCents === null ? "No usable price" : formatCents(candidate.priceCents),
    ]
      .filter((part) => part !== "")
      .join(", ");
    const pick = button(
      label,
      "price-list-fill-pick",
      () => {
        if (state === undefined) return;
        state.choices = setFill(state.choices, row.productId, candidate.ncCode);
        render();
      },
      `pl-opt${isChosen ? " pl-opt--on" : ""}`,
      busy || taken,
    );
    pick.setAttribute("role", "radio");
    pick.setAttribute("aria-checked", isChosen ? "true" : "false");
    pick.dataset["offerId"] = row.productId;
    picks.append(pick);
  }
  const sw = h("button", {
    cls: "pl-switch pl-p-sw",
    testid: "price-list-fill-switch",
    attrs: {
      type: "button",
      role: "switch",
      "aria-checked": on ? "true" : "false",
      "aria-label": `${on ? "Add" : "Skip"} the NC code for ${row.productName}`,
    },
  }) as HTMLButtonElement;
  sw.dataset["offerId"] = row.productId;
  // With several codes to choose from, the code is picked first; the switch then only turns it off.
  sw.disabled = busy || (!on && candidates.length !== 1);
  sw.append(
    h("span", { cls: "pl-switch-track" }, [h("span", { cls: "pl-switch-knob" })]),
    h("span", { cls: "pl-switch-text", text: on ? "Add" : "Skip" }),
  );
  sw.addEventListener("click", () => {
    if (state === undefined) return;
    const only = candidates[0];
    state.choices = setFill(
      state.choices,
      row.productId,
      on || only === undefined || candidates.length !== 1 ? null : only.ncCode,
    );
    render();
  });
  return h("li", { cls: "pl-issue", testid: "price-list-fill-row" }, [
    h("div", {}, [
      h("div", { cls: "pl-issue-name", text: row.productName }),
      h("div", {
        cls: "pl-p-sub",
        text: row.itemSizeLabel === null ? "" : `Item size ${row.itemSizeLabel}.`,
      }),
      picks,
    ]),
    sw,
  ]);
}

function simpleRowsBody(
  s: State,
  key: string,
  intro: string,
  rows: PriceListRowDto[],
  pill: string,
  detail: (row: PriceListRowDto) => string,
): Node[] {
  const nodes: Node[] = [h("p", { cls: "pl-section-p", text: intro })];
  if (rows.length === 0) {
    nodes.push(h("p", { cls: "pl-section-p", text: "Nothing here.", testid: "price-list-empty" }));
    return nodes;
  }
  const list = h("ul", { cls: "pl-issue-list", testid: `price-list-list-${key}` });
  for (const row of rows.slice(0, shownFor(s, key))) {
    list.append(
      h("li", { cls: "pl-issue", testid: "price-list-card" }, [
        h("div", {}, [
          h("div", { cls: "pl-issue-name", text: row.productName }),
          h("div", { cls: "pl-p-sub", text: detail(row) }),
        ]),
        h("span", { cls: "pl-chip pl-chip--info", text: pill }),
      ]),
    );
  }
  nodes.push(list, showMoreRow(s, key, rows.length, "row"));
  return nodes;
}

function problemsBody(s: State, preview: PriceListPreviewDto): Node[] {
  const rows = rowsInGroup(preview, "problem");
  const nodes: Node[] = [
    h("p", {
      cls: "pl-section-p",
      text: "These rows cannot be used, so those items keep the price they have now. Fix the file and choose it again, or change the price by hand in the item.",
    }),
  ];
  if (rows.length === 0) {
    nodes.push(
      h("p", {
        cls: "pl-section-p",
        text: "No problems in this file.",
        testid: "price-list-empty",
      }),
    );
    return nodes;
  }
  const list = h("ul", { cls: "pl-issue-list", testid: "price-list-list-problems" });
  for (const row of rows.slice(0, shownFor(s, "problems"))) {
    list.append(
      h("li", { cls: "pl-issue pl-issue--bad", testid: "price-list-problem" }, [
        h("div", {}, [
          h("div", { cls: "pl-issue-name", text: row.productName }),
          h("div", {
            cls: "pl-issue-why",
            text: row.problem?.message ?? "",
            testid: "price-list-problem-why",
          }),
          h(
            "div",
            { cls: "pl-issue-fact" },
            problemRowDetail(row).map((fact) => h("span", { cls: "pl-fact", text: fact })),
          ),
        ]),
        h("span", { cls: "pl-chip pl-chip--warn", text: "Skipped" }),
      ]),
    );
  }
  nodes.push(list, showMoreRow(s, "problems", rows.length, "problem"));
  nodes.push(
    h("div", { cls: "pl-cta-row" }, [
      button(
        "Back to the price changes",
        "price-list-back-to-changes",
        () => void chooseTab("changes"),
        "pl-btn-primary",
      ),
    ]),
  );
  return nodes;
}

function noCodeBody(s: State, preview: PriceListPreviewDto): Node[] {
  const nodes: Node[] = [
    h("p", {
      cls: "pl-section-p",
      text: `These items have no NC code on ${s.vendor.name}, so a file cannot find them. Add the code in the item's Stock and vendor section, then choose the file again.`,
    }),
  ];
  if (preview.noCode.length === 0) {
    nodes.push(
      h("p", { cls: "pl-section-p", text: "Every item has a code.", testid: "price-list-empty" }),
    );
    return nodes;
  }
  const list = h("ul", { cls: "pl-issue-list", testid: "price-list-list-nocode" });
  for (const row of preview.noCode.slice(0, shownFor(s, "nocode"))) {
    list.append(
      h("li", { cls: "pl-issue", testid: "price-list-card" }, [
        h("div", {}, [
          h("div", { cls: "pl-issue-name", text: row.productName }),
          h("div", {
            cls: "pl-p-sub",
            text:
              row.reason === "no_offer"
                ? `Active. No ${s.vendor.name} offer.`
                : `Active. ${s.vendor.name} offer has no code.`,
          }),
        ]),
        h("span", { cls: "pl-chip pl-chip--info", text: "No code" }),
      ]),
    );
  }
  nodes.push(list, showMoreRow(s, "nocode", preview.noCode.length, "item"));
  return nodes;
}

function ignoredBody(s: State): Node[] {
  const label = PRICE_COLUMN_LABEL;
  const nodes: Node[] = [
    h("p", {
      cls: "pl-section-p",
      text: "These rows in the file are items you do not stock. They are ignored. This import never adds items to your catalog.",
    }),
  ];
  if (s.ignored.total === 0) {
    nodes.push(
      h("p", { cls: "pl-section-p", text: "Nothing is ignored.", testid: "price-list-empty" }),
    );
    return nodes;
  }
  const list = h("ul", { cls: "pl-issue-list", testid: "price-list-list-ignored" });
  for (const row of s.ignored.rows) {
    list.append(
      h("li", { cls: "pl-issue", testid: "price-list-card" }, [
        h("div", {}, [
          h("div", { cls: "pl-issue-name", text: row.brandName ?? `NC ${row.ncCode}` }),
          h("div", {
            cls: "pl-p-sub",
            text: `${ignoredLine(row, label)}${row.sizeLabel === null ? "" : ` ${row.sizeLabel}.`}`,
          }),
        ]),
        h("span", { cls: "pl-chip pl-chip--info", text: "Ignored" }),
      ]),
    );
  }
  nodes.push(list);
  const more = h("div", { cls: "pl-more-row" }, [
    h("span", {
      text: `Showing ${s.ignored.rows.length.toLocaleString("en-US")} of ${plural(s.ignored.total, "ignored row", "ignored rows")}.`,
      testid: "price-list-showing",
    }),
  ]);
  if (s.ignored.next !== null) {
    const next = button(
      s.ignored.loading ? "Loading" : "Show 25 more",
      "price-list-show-more",
      () => void loadMoreIgnored(),
      "pl-btn",
      s.ignored.loading,
    );
    more.append(next);
  }
  nodes.push(more);
  return nodes;
}

function updateSummaryText(counts: ReturnType<typeof selectionCounts>): string {
  const prices = plural(counts.updating, "price will be updated", "prices will be updated");
  return counts.skuFills === 0
    ? prices
    : `${prices}, ${plural(counts.skuFills, "NC code will be added", "NC codes will be added")}`;
}

function applyLocation(
  preview: PriceListPreviewDto,
  counts: ReturnType<typeof selectionCounts>,
  busy: boolean,
): HTMLElement {
  const held = counts.heldBack;
  const sub =
    held > 0
      ? `${plural(held, "held for a look stays", "held for a look stay")} at ${held === 1 ? "its" : "their"} price now. Switch any row to Skip to leave it out.`
      : "Switch any row to Skip to leave it out.";
  const text = h("div", { cls: "pl-ab-text" }, [
    h("div", {
      cls: "pl-ab-main",
      text: updateSummaryText(counts),
      testid: "price-list-apply-summary",
    }),
    h("div", { cls: "pl-ab-sub", text: sub }),
  ]);
  const actions = h("div", { cls: "pl-ab-actions" }, [
    button(
      "Choose a different file",
      "price-list-choose-other",
      () => {
        if (state === undefined) return;
        state.preview = undefined;
        state.file = undefined;
        state.phase = "choose";
        render();
      },
      "pl-btn",
      busy,
    ),
    button(
      busy ? "Updating" : "Review and update",
      "price-list-review",
      () => void reviewAndUpdate(),
      "pl-btn-primary",
      busy || counts.updating + counts.skuFills === 0 || !preview.gate.ok,
    ),
  ]);
  return h("div", { cls: "pl-applybar", testid: "price-list-apply-bar" }, [text, actions]);
}

// ---------------------------------------------------------------------
// Step 3: confirm, update, result
// ---------------------------------------------------------------------

function confirmBody(s: State, preview: PriceListPreviewDto): HTMLElement {
  const counts = selectionCounts(preview, s.choices);
  const rows: Array<[string, string, number]> = [
    ["Prices that go up", "", counts.up],
    ["Prices that go down", "", counts.down],
  ];
  if (counts.firstPrice > 0) {
    rows.push(["First prices (no price before)", "", counts.firstPrice]);
  }
  rows.push([
    "Held back, price stays the same",
    `${counts.heldBack} checked first, ${counts.problems} with a problem`,
    counts.heldBack + counts.problems,
  ]);
  rows.push(["Items you switched to Skip", "", counts.skippedByUser]);
  if (counts.skuFills > 0) {
    rows.push(["NC codes added to items", "", counts.skuFills]);
  }
  const table = h("table", { cls: "pl-sum-table", testid: "price-list-confirm-table" });
  for (const [label, sub, n] of rows) {
    const first = h("td", {}, [label]);
    if (sub !== "") first.append(h("span", { cls: "pl-sum-sub", text: sub }));
    table.append(h("tr", {}, [first, h("td", { text: String(n) })]));
  }
  return h("div", {}, [
    table,
    h("div", { cls: "pl-note pl-note--info" }, [
      icon("info"),
      h("p", {
        text: "Open carts that are not placed yet will show the new prices. Orders already placed keep their totals. Stock and average cost do not change.",
      }),
    ]),
  ]);
}

async function reviewAndUpdate(): Promise<void> {
  const s = state;
  if (
    s === undefined ||
    s.preview === undefined ||
    hooks === undefined ||
    s.originalBody !== undefined ||
    !currentActor(s)
  )
    return;
  const counts = selectionCounts(s.preview, s.choices);
  if (counts.updating + counts.skuFills === 0) return;
  const gen = generation;
  const what =
    counts.skuFills === 0
      ? plural(counts.updating, "price", "prices")
      : counts.updating === 0
        ? plural(counts.skuFills, "NC code", "NC codes")
        : `${plural(counts.updating, "price", "prices")} and ${plural(counts.skuFills, "NC code", "NC codes")}`;
  const confirmed = await hooks.confirm({
    title: `Update ${what} on ${s.vendor.name}?`,
    message: `This saves the changes from ${s.preview.file.name}.`,
    confirmLabel: `Update ${what}`,
    cancelLabel: "Go back",
    tone: "accent",
    extra: confirmBody(s, s.preview),
  });
  if (!confirmed || !isCurrent(gen)) return;
  if (state !== undefined && state.applyKey === undefined) {
    state.applyKey = crypto.randomUUID();
  }
  await applyNow();
}

async function applyNow(): Promise<void> {
  const s = state;
  if (s === undefined || s.preview === undefined || s.applyKey === undefined || !currentActor(s))
    return;
  const key = scopeKey(s.session, s.locationId, s.vendor.id);
  if (s.originalBody === undefined) {
    s.originalBody = structuredClone({
      ...applyBody(s.preview, s.choices, s.applyKey),
      ...(s.locationId === undefined ? {} : { locationId: s.locationId }),
    });
    originalApplies.set(key, s);
  }
  if (s.phase === "applying") return;
  s.phase = "applying";
  s.error = undefined;
  render();
  const result = await applyVendorPriceList(
    s.session.apiBaseUrl,
    s.session.tenantId,
    s.vendor.id,
    s.originalBody,
  );
  if (
    result.status !== 200 ||
    result.body.vendorId !== s.vendor.id ||
    typeof result.body.importId !== "string"
  ) {
    s.phase = "error";
    const failure = errorView(result.status, result.networkError, result.body, "update");
    s.error = { ...failure, step: "update" };
    // An expired check is a definite refusal: nothing was saved and the same request can never succeed. Keeping
    // it as the "original update" would leave only Retry original update, which fails the same way every time and
    // blocks checking the file again. An unknown outcome (a dropped connection, a server error) keeps it.
    if (failure.kind === "expired") {
      s.originalBody = undefined;
      originalApplies.delete(key);
    }
  } else {
    s.result = result.body;
    s.phase = "done";
    s.originalBody = undefined;
    originalApplies.delete(key);
    lastImports.delete(s.vendor.id);
  }
  if (state !== s || !currentActor(s)) return;
  render();
  if (s.phase === "done") scrollToTop();
}

function resultRow(
  label: string,
  value: number,
  sub: string | undefined,
  testid: string,
): HTMLElement {
  const text = h("div", {}, [label]);
  if (sub !== undefined) text.append(h("div", { cls: "pl-p-sub", text: sub }));
  return h("div", { cls: "pl-result-row", testid }, [text, h("strong", { text: String(value) })]);
}

function doneNodes(s: State): Node[] {
  const result = s.result;
  if (result === undefined) return [];
  const nodes: Node[] = [];
  const stale = result.changedSinceCheck;
  const heading =
    stale.length > 0
      ? `${plural(result.updated, "updated", "updated")}, ${stale.length} skipped`
      : `${plural(result.updated, "price updated", "prices updated")}`;
  const hero = h("div", { cls: "pl-done-hero" }, [
    h("div", { cls: "pl-done-ico" }, [icon("check-circle", true)]),
    h("div", {}, [
      h("h2", { cls: "pl-done-title", text: heading, testid: "price-list-done-title" }),
      h("p", {
        text:
          stale.length > 0
            ? `Someone changed the price of ${plural(stale.length, "item", "items")} after you checked. We left ${stale.length === 1 ? "that one" : "those"} alone so we do not overwrite their work.`
            : `${s.vendor.name} now lists the new prices. Orders you start from here on use them.`,
      }),
    ]),
  ]);
  const card = h("section", { cls: "pl-card", testid: "price-list-done" }, [hero]);
  if (stale.length > 0) {
    const list = h("ul", { cls: "pl-plain-list", testid: "price-list-stale-list" });
    for (const entry of stale) {
      list.append(h("li", { text: staleLine(entry) }));
    }
    card.append(list);
  }
  const heldHelp =
    result.heldNotTaken > 0
      ? "They kept their price. Choose the file again to take them."
      : undefined;
  card.append(
    h("div", { cls: "pl-result-rows" }, [
      resultRow("Prices updated", result.updated, undefined, "price-list-done-updated"),
      ...((result.skuFilled ?? 0) > 0
        ? [
            resultRow(
              "NC codes added",
              result.skuFilled ?? 0,
              undefined,
              "price-list-done-skufilled",
            ),
          ]
        : []),
      resultRow("Held back for a look", result.heldNotTaken, heldHelp, "price-list-done-held"),
      resultRow(
        "Skipped because you switched them to Skip",
        result.skippedByUser,
        undefined,
        "price-list-done-skipped",
      ),
      resultRow(
        "Skipped because of a problem",
        result.problems,
        undefined,
        "price-list-done-problems",
      ),
      resultRow(
        "Skipped because the price changed after you checked",
        stale.length,
        undefined,
        "price-list-done-stale",
      ),
      resultRow("No change needed", result.noChange, undefined, "price-list-done-nochange"),
    ]),
  );
  card.append(
    h("div", { cls: "pl-audit", testid: "price-list-saved-line" }, [
      h("strong", { text: "Saved in the history: " }),
      `${s.session.employeeId} updated ${plural(result.updated, "price", "prices")} on ${s.vendor.name} from ${result.fileName} on ${dateTimeText(result.createdAt)}.`,
    ]),
  );
  const actions = h("div", { cls: "pl-cta-row" }, [
    button("Done", "price-list-done-button", closeScreen, "pl-btn-primary"),
    button(
      "Download the list of changes (CSV)",
      "price-list-download",
      () => void downloadCsv(),
      "pl-btn",
    ),
  ]);
  if (stale.length > 0) {
    actions.prepend(
      button(
        "Check the file again",
        "price-list-check-again",
        () => {
          if (state === undefined) return;
          state.result = undefined;
          if (state.file !== undefined) {
            void runPreview();
          } else {
            state.phase = "choose";
            render();
          }
        },
        "pl-btn-primary",
      ),
    );
    (actions.querySelector('[data-testid="price-list-done-button"]') as HTMLElement).className =
      "pl-btn";
  }
  card.append(actions);
  nodes.push(card);
  nodes.push(
    h("section", { cls: "pl-card" }, [
      h("h2", { text: "Still the same as before" }),
      neverList(s.vendor.name),
    ]),
  );
  return nodes;
}

async function downloadCsv(): Promise<void> {
  const s = state;
  if (s === undefined || s.result === undefined) return;
  const result = await getVendorPriceListChangesCsv(
    s.session.apiBaseUrl,
    s.session.tenantId,
    s.vendor.id,
    s.result.importId,
  );
  if (result.status !== 200 || result.blob === undefined) {
    hooks?.toast(
      "Could not download the list of changes. Check your connection and try again.",
      "danger",
    );
    return;
  }
  const url = URL.createObjectURL(result.blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `price-list-changes-${s.vendor.name.replace(/[^A-Za-z0-9]+/g, "-")}.csv`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
